"use client";

import ScrollReveal from "@/components/common/ScrollReveal";
import PageTitle from "@/components/common/PageTitle";

const Cookie = () => {
  return (
    <div className="flex flex-col gap-8 sm:gap-10 lg:gap-12">
      {/* Same 56px under the rule as the Privacy page and as every tabbed
          subpage leaves under its tab row (48px layout + pt-2). */}
      <div className="flex flex-col gap-4 md:pb-4 lg:pb-2">
        {/* Page title in the site's product-page style: 5px red accent bar at the
            content margin, title text nudged past it.

            Deliberately outside the ScrollReveal below: PageTitle brings its own
            entrance (the scan reveal every page title on the site now uses), and
            nesting it in a fade-up would run two entrances over the one heading. */}
        <PageTitle>ECQ Cookies Policy</PageTitle>

        <ScrollReveal direction="up" delay={0}>
          <div className="h-[1px] w-full border-[1px] border-neutral-500"></div>
        </ScrollReveal>
      </div>

      <ScrollReveal direction="up" delay={100}>
        <p className="body-1 text-text-highlight">
          This Cookie Policy explains how ECQ uses cookies and similar tracking technologies on our
          website. By accessing or using our website, you acknowledge that you have read and
          understood this Cookie Policy.
        </p>
      </ScrollReveal>

      <ScrollReveal direction="up" delay={200}>
        <section className="flex flex-col gap-2">
          <h2 className="font-bold text-[24px] sm:text-[28px] lg:text-[30px]">What Are Cookies?</h2>
          <div>
            <p className="body-1">
              Cookies are small text files that are placed on your device (computer, smartphone, or
              tablet) when you visit a website. They are widely used to make websites work more
              efficiently and to provide a better user experience. Cookies enable websites to
              remember your preferences, track your activities, and analyze how the website is being
              used.
            </p>
          </div>
        </section>
      </ScrollReveal>

      <ScrollReveal direction="up" delay={300}>
        <section className="flex flex-col gap-2">
          <h2 className="font-bold text-[24px] sm:text-[28px] lg:text-[30px]">
            Type of Cookies We Use
          </h2>
          <div>
            <p className="body-1">We use the following types of cookies on our website:</p>
            <ol className="body-1 space-y-4">
              <li>
                <strong className="body-1-heavy text-primary-600">2.1. Necessary Cookies:</strong>{" "}
                <span className="body-1 text-text-highlight block mt-2">
                  These cookies are essential for the functioning of our website and cannot be
                  disabled in our systems. They are usually set in response to your actions, such as
                  setting your privacy preferences, logging in, or completing forms. You can set
                  your browser to block or alert you about these cookies, but some parts of the
                  website may not function properly without them.
                </span>
              </li>
              <li>
                <strong className="body-1-heavy text-primary-600">
                  2.2. Analytics and Performance Cookies:
                </strong>{" "}
                <span className="body-1 text-text-highlight block mt-2">
                  These cookies allow us to collect information about how visitors use our website,
                  including the number of visitors, the pages they visit, and the source of traffic.
                  This helps us improve the performance and usability of our website. The
                  information collected is aggregated and anonymized. If you do not consent to the
                  use of these cookies, we will not be able to track website usage to improve our
                  services.
                </span>
              </li>
              <li>
                <strong className="body-1-heavy text-primary-600">2.3. Functional Cookies:</strong>{" "}
                <span className="body-1 text-text-highlight block mt-2">
                  These cookies enable our website to provide enhanced functionality and
                  personalization. They may be set by us or by third-party providers whose services
                  we have added to our pages. If you do not consent to the use of these cookies,
                  some or all of these services may not function properly.
                </span>
              </li>
              <li>
                <strong className="body-1-heavy text-primary-600">
                  2.4. Advertising and Targeting Cookies:
                </strong>{" "}
                <span className="body-1 text-text-highlight block mt-2">
                  These cookies are used to deliver relevant advertisements and content to you based
                  on your interests and browsing history. They are also used to limit the number of
                  times you see an advertisement and measure the effectiveness of advertising
                  campaigns. These cookies are usually placed by advertising networks with our
                  permission. If you do not consent to the use of these cookies, you may still see
                  advertisements, but they will be less relevant to you.
                </span>
              </li>
            </ol>
          </div>
        </section>
      </ScrollReveal>

      <ScrollReveal direction="up" delay={400}>
        <section className="flex flex-col gap-2">
          <h2 className="font-bold text-[24px] sm:text-[28px] lg:text-[30px]">
            Third-Party Cookies
          </h2>
          <p className="body-1 text-text-highlight">
            We may allow third-party service providers, such as analytics or advertising companies,
            to use cookies on our website. These providers may collect information about your online
            activities over time and across different websites when you use our website. We do not
            have control over these third-party cookies and their use is subject to their respective
            privacy policies.
          </p>
        </section>
      </ScrollReveal>

      <ScrollReveal direction="up" delay={0}>
        <section className="flex flex-col gap-2">
          <h2 className="font-bold text-[24px] sm:text-[28px] lg:text-[30px]">
            Your Cookies Choices
          </h2>
          {/* The section's gap-2 sets heading→copy; the paragraphs need the
              site-wide 24/28px between themselves, which gap-2 + mt-4 fell 4px
              short of. Own wrapper keeps the heading spacing untouched. */}
          <div className="flex flex-col gap-6 sm:gap-7">
            <p className="body-1 text-text-highlight">
              Most web browsers are set to accept cookies by default. However, you can choose to
              block or delete cookies through your browser settings. Please note that blocking or
              deleting cookies may affect the functionality and usability of our website.
            </p>

            <p className="body-1 text-text-highlight">
              To manage your cookie preferences, you can typically find the cookie settings in your
              browser&apos;s &quot;Options&quot; or &quot;Preferences&quot; menu. You can also refer
              to the &quot;Help&quot; section of your browser for more information.
            </p>
          </div>
        </section>
      </ScrollReveal>

      <ScrollReveal direction="up" delay={100}>
        <section className="flex flex-col gap-2">
          <h2 className="font-bold text-[24px] sm:text-[28px] lg:text-[30px]">
            Updates to this Cookie Policy
          </h2>
          <p className="body-1 text-text-highlight">
            We may update this Cookie Policy from time to time to reflect changes in our cookie
            practices. We will notify you of any material changes by posting the updated Cookie
            Policy on our website or through other appropriate communication channels. We encourage
            you to review this Cookie Policy periodically for any updates.
          </p>
        </section>
      </ScrollReveal>

      <ScrollReveal direction="up" delay={200}>
        <section className="flex flex-col gap-2">
          <h2 className="font-bold text-[24px] sm:text-[28px] lg:text-[30px]">Contact Us</h2>
          <p className="body-1 text-text-highlight">
            If you have any questions, concerns, or requests regarding this Privacy Policy or the
            handling of your personal information, please contact us at:
          </p>

          <div className="flex flex-col gap-4 mt-4">
            <div className="flex flex-row items-center gap-4 sm:gap-9">
              <svg
                width="101"
                height="38"
                viewBox="0 0 101 38"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-20 sm:w-[101px] h-auto flex-shrink-0"
              >
                <mask
                  id="mask0_694_66527"
                  style={{ maskType: "luminance" }}
                  maskUnits="userSpaceOnUse"
                  x="59"
                  y="0"
                  width="42"
                  height="38"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M59.2227 0.304688H101V37.926H59.2227V0.304688Z"
                    fill="white"
                  />
                </mask>
                <g mask="url(#mask0_694_66527)">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M89.9226 5.81576C93.3375 9.21112 95.45 13.9127 95.4506 19.115C95.4506 22.7482 94.4212 26.1339 92.6385 29.0035L100.973 37.8988L89.858 37.9271L87.007 34.7869C84.015 36.761 80.4172 37.9116 76.5445 37.9123C71.3088 37.9123 66.5793 35.8116 63.1639 32.4168C61.5169 30.7796 60.1732 28.8376 59.2227 26.6836H68.2173C68.3311 26.807 68.4468 26.9278 68.5664 27.0461C70.6169 29.0851 73.4381 30.3457 76.5426 30.3457C79.649 30.3457 82.4696 29.0851 84.5194 27.0467C86.5705 25.0077 87.8398 22.2031 87.8398 19.1169C87.8398 16.0288 86.5705 13.2249 84.52 11.1865C82.4702 9.14748 79.6483 7.88628 76.5432 7.88628C73.4381 7.88628 70.6169 9.14748 68.5664 11.1859C68.4475 11.3048 68.3311 11.4256 68.2173 11.5491H59.2227C60.1732 9.39433 61.5169 7.45302 63.1639 5.81576C66.5793 2.42104 71.3088 0.320955 76.5426 0.320313C81.7777 0.320955 86.5071 2.42104 89.9226 5.81576Z"
                    fill="#78797D"
                  />
                </g>
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M49.4121 7.65705H49.4114V7.64214C46.2612 7.64214 43.3977 8.91357 41.3171 10.9691C39.2364 13.0233 37.949 15.8507 37.949 18.9618H37.9641V18.9631H37.949C37.949 22.0749 39.237 24.9023 41.3177 26.9571C43.3984 29.012 46.2612 30.2834 49.4108 30.2834V30.2685H49.4121H49.4127V30.2834C52.5629 30.2834 55.4264 29.012 57.5064 26.9565C57.6278 26.8379 57.7453 26.7161 57.8608 26.591H66.9879C66.0227 28.7632 64.6599 30.7202 62.9886 32.3707C59.5228 35.7936 54.7237 37.9113 49.4127 37.9113V37.9262H49.4121H49.4108V37.9113C44.1005 37.9107 39.3013 35.7936 35.8355 32.3714C32.3704 28.9485 30.226 24.2089 30.226 18.9631H30.2109V18.9618H30.226C30.226 13.7174 32.3704 8.97707 35.8355 5.55486C39.3007 2.13265 44.1005 0.0155526 49.4114 0.0149045V0H49.4121H49.4134V0.0149045C54.7237 0.0155526 59.5228 2.13265 62.988 5.55421C64.6599 7.20473 66.0227 9.16241 66.9879 11.3339H57.8608C57.7453 11.2095 57.6272 11.0877 57.5064 10.9684C55.4258 8.91292 52.5629 7.64214 49.4134 7.64214V7.65705H49.4121Z"
                  fill="#B1B2B7"
                />
                <mask
                  id="mask1_694_66527"
                  style={{ maskType: "luminance" }}
                  maskUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width="40"
                  height="38"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M0 0.304688H39.0872V37.926H0V0.304688Z"
                    fill="white"
                  />
                </mask>
                <g mask="url(#mask1_694_66527)">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M19.5437 0.304688H19.5443V0.319472C24.95 0.320115 29.8354 2.4202 33.3624 5.81492C36.8894 9.21029 39.072 13.9119 39.072 19.1142H39.0873V19.1155V19.1161H39.072C39.072 20.4911 38.919 21.8314 38.6298 23.1215H34.5612H30.4385H24.1726H19.5764H19.5457H8.64882C9.24188 24.6038 10.1535 25.9377 11.3049 27.0453C13.4227 29.0843 16.3366 30.3448 19.543 30.3448V30.3301H19.5437V37.9262H19.543V37.9115C14.1373 37.9115 9.25257 35.8107 5.72492 32.416C2.19794 29.0206 0.0160287 24.3191 0.0153608 19.1168H0V19.1155V19.1148H0.0153608C0.0160287 13.9119 2.19794 9.21029 5.72559 5.81492C9.25257 2.4202 14.1373 0.320115 19.5423 0.319472V0.304688H19.5437ZM30.4389 15.1088C29.8458 13.6264 28.9335 12.2926 27.7828 11.185C25.665 9.14601 22.7511 7.8848 19.544 7.8848V7.89958H19.5433H19.542V7.8848C16.3363 7.8848 13.4224 9.14601 11.3046 11.1844C10.1532 12.2926 9.24156 13.6264 8.6485 15.1088H19.5453H19.5834H24.1723H30.4389Z"
                    fill="#EC1938"
                  />
                </g>
              </svg>
            </div>

            <div className="flex flex-row items-center gap-4 sm:gap-9">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-neutral-900">
                <svg
                  width="12"
                  height="10"
                  viewBox="0 0 12 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g id="email copy">
                    <path
                      id="Shape"
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M10.8 0.195312H1.2C0.54 0.195312 0 0.735313 0 1.39531V8.59531C0 9.25531 0.54 9.79531 1.2 9.79531H10.8C11.46 9.79531 12 9.25531 12 8.59531V1.39531C12 0.735313 11.46 0.195312 10.8 0.195312ZM10.8001 2.59648L6.00007 5.59648L1.20007 2.59648V1.39648L6.00007 4.39648L10.8001 1.39648V2.59648Z"
                      fill="#EC1938"
                    />
                  </g>
                </svg>
              </div>
              <a
                href="mailto:DPO@e-cq.net"
                className="body-1 break-all hover:text-primary transition-colors duration-300"
              >
                DPO@e-cq.net
              </a>
            </div>
          </div>
        </section>
      </ScrollReveal>
    </div>
  );
};

export default Cookie;
